import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Heart, Eye, EyeOff } from "lucide-react";
import axios from "axios";
import { BRAND } from "../../components/common/brand";
import { COLORS } from "../../components/common/color_pallate";

const LoginForm = () => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");

  // Validation
  const validateField = (name, value) => {
    switch (name) {
      case "email":
        if (!value) return "Email is required";
        if (!/\S+@\S+\.\S+/.test(value)) return "Invalid email format";
        return "";
      case "password":
        if (!value) return "Password is required";
        if (value.length < 6) return "Minimum 6 characters required";
        return "";
      default:
        return "";
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };


  const handleBlur = (e) => {
    const { name } = e.target;
    const value =
      name === "email" ? formData.email : formData[name];
    setTouched((prev) => ({ ...prev, [name]: true }));
    setErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
  };

  useEffect(() => {
    const hasErrors = Object.values(errors).some(Boolean);
    const filled = formData.email && formData.password;

    setIsValid(!hasErrors && filled);
  }, [errors, formData]);

  // Submit
  const API = process.env.REACT_APP_API_URL || "";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid) return;
    setIsSubmitting(true);
    setError("");

    try {
      const res = await axios.post(
        `${API}/api/users/login`,
        { email: formData.email, password: formData.password },
        { headers: { "Content-Type": "application/json" } }
      );

      if (res.status === 200) {
        // store token (so subsequent requests can use it)
        localStorage.setItem("token", res.data.token);
        localStorage.setItem("userId", String(res.data.userId || ""));
        localStorage.setItem("fullName", res.data.fullName || "");
        localStorage.setItem("role", res.data.role || "");
        
        // redirect to appropriate dashboard based on role
        if (res.data.role === "ADMIN") {
          window.location.href = "/admin";
        } else {
          window.location.href = "/";
        }
      }
    } catch (err) {
      setError(
        err.response?.data ||
        (err.response?.status === 401 ? "Invalid email or password" : "Something went wrong")
      );
    } finally {
      setIsSubmitting(false);
    }
  };


  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-6 relative"
      style={{
        background: `linear-gradient(135deg, ${COLORS.background} 0%, ${COLORS.surface} 100%)`,
        backgroundAttachment: "fixed",
      }}
    >
      {/* Subtle decorative background */}
      <div
        className="absolute inset-0 opacity-[0.04] bg-[url('https://www.transparenttextures.com/patterns/paw-prints.png')] bg-repeat pointer-events-none"
        style={{ backgroundSize: "180px" }}
      ></div>

      {/* Card Container */}
      <div className="relative flex flex-col lg:flex-row w-full max-w-5xl rounded-3xl overflow-hidden shadow-2xl bg-surface my-2 border border-border/40">
        {/* LEFT Gradient Panel */}
        <div 
          className="hidden lg:flex lg:w-1/2 relative"
          style={{ background: `linear-gradient(135deg, ${COLORS.gradient.from}, ${COLORS.gradient.to})` }}
        >
          {/* Subtle overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>

          <div className="relative flex flex-col justify-between p-10 text-surface z-10">
            <div>
              {/* Logo */}
              <Link to="/" className="inline-flex items-center gap-3 mb-8 group">
                <div className="bg-white/20 backdrop-blur-sm p-2.5 rounded-xl shadow-lg group-hover:scale-105 transition-transform">
                  <Heart className="h-5 w-5 text-surface" />
                </div>
                <div>
                  <h1 className="text-lg font-bold">{BRAND.name}</h1>
                  <p className="text-xs text-secondary">Pet Paradise</p>
                </div>
              </Link>

              {/* Hero Content */}
              <h2 className="text-3xl font-bold leading-snug mb-4">
                Welcome back to your pet's happy place 🐾
              </h2>
              <p className="text-secondary text-sm leading-relaxed">
                Sign in to continue exploring premium toys, treats, and accessories for your furry friends.
              </p>
            </div>

            {/* Footer */}
            <p className="text-xs text-secondary/70 mt-6">
              © 2025 {BRAND.name}. All rights reserved.
            </p>
          </div>
        </div>

        {/* RIGHT FORM */}
        <div className="w-full lg:w-1/2 bg-surface p-6 sm:p-8 lg:p-10 flex flex-col justify-center">
          <div className="max-w-sm w-full mx-auto">
            {/* Mobile Logo */}
            <Link to="/" className="lg:hidden flex items-center justify-center gap-2 mb-6">
              <div className="bg-primary p-2 rounded-xl">
                <Heart className="h-5 w-5 text-surface" />
              </div>
              <span className="text-xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                {BRAND.name}
              </span>
            </Link>

            <div className="text-center lg:text-left">
              <h2 className="text-2xl font-bold text-text mb-1.5">
                Sign in to your account
              </h2>
              <p className="text-sm text-textMuted mb-6">
                Don't have an account?{" "}
                <Link
                  to="/register"
                  className="text-primary font-semibold hover:text-accent transition-colors"
                >
                  Sign up here
                </Link>
              </p>
            </div>

            {/* Error Alert */}
            {error && (
              <div className="p-3 mb-4 bg-red-50 border-l-4 border-red-500 rounded-lg">
                <p className="text-xs text-red-700 font-medium">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Email */}
              <div>
                <label className="block text-xs font-semibold text-text mb-1.5">
                  Email address
                </label>
                <input
                  type="email"
                  name="email"
                  autoFocus
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="you@example.com"
                  className={`w-full px-4 py-2.5 text-sm rounded-xl border-2 transition-all focus:outline-none bg-surface text-text ${errors.email
                    ? "border-red-300 bg-red-50 focus:border-red-500"
                    : touched.email && !errors.email
                      ? "border-green-300 bg-green-50 focus:border-green-500"
                      : "border-border focus:border-primary focus:ring-2 focus:ring-secondary/50"
                    }`}
                />
                {errors.email && (
                  <p className="text-xs text-red-600 mt-1">{errors.email}</p>
                )}
              </div>

              {/* Password */}
              <div>
                <label className="block text-xs font-semibold text-text mb-1.5">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Enter your password"
                    className={`w-full px-4 py-2.5 pr-12 text-sm rounded-xl border-2 transition-all focus:outline-none bg-surface text-text ${errors.password
                      ? "border-red-300 bg-red-50 focus:border-red-500"
                      : touched.password && !errors.password
                        ? "border-green-300 bg-green-50 focus:border-green-500"
                        : "border-border focus:border-primary focus:ring-2 focus:ring-secondary/50"
                      }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-textMuted hover:text-text transition-colors"
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-xs text-red-600 mt-1">{errors.password}</p>
                )}
              </div>

              {/* Options */}
              <div className="flex items-center justify-between text-sm pt-1">
                <label className="flex items-center gap-2 text-text cursor-pointer">
                  <input
                    type="checkbox"
                    className="h-4 w-4 text-primary focus:ring-2 focus:ring-primary border-border rounded"
                  />
                  <span className="select-none">Remember me</span>
                </label>
                <Link
                  to="/forgot-password"
                  className="text-primary hover:text-accent font-semibold transition-colors"
                >
                  Forgot password?
                </Link>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={!isValid || isSubmitting}
                className={`w-full py-3 rounded-xl text-surface font-semibold text-sm transition-all ${!isValid || isSubmitting
                  ? "bg-secondary text-textMuted cursor-not-allowed"
                  : "bg-primary hover:bg-primary/90 hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]"
                  }`}
              >
                {isSubmitting ? (
                  <div className="flex justify-center items-center gap-2">
                    <svg
                      className="animate-spin h-4 w-4 text-surface"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                      ></path>
                    </svg>
                    Signing in...
                  </div>
                ) : (
                  "Sign in"
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;